import { EntityBase } from '@provision/entity-types';
import { ColoredAvatar, DynamicIcon } from 'src/components';
import { extractDisabledUserTextFromName } from 'src/graphql/entities/platform-user/utils';
import {
  formatServiceLevelWithCpp,
  getRowCppExpDate,
} from 'src/graphql/entities/service-level/utils';
import { AttributeValue } from 'src/pages/CustomViews/custom-views-entities-table/attribute-value';
import {
  CustomComponentsByAttribute,
  getAttributeRawValue,
} from 'src/pages/CustomViews/custom-views-entities-table/render-attribute-value';
import {
  CardColumns,
  CardLayoutRoles,
  findSlotFallback,
  SlotFallback,
  SlotVisual,
} from './mobile-card-layout';
import { ServiceLevelDot, SLOT_DOT_SIZE, SlotCheck } from './mobile-card-list.elements';

const SLOT_ICON_SIZE_PX = 18;
const SLOT_CHECK_ICON_SIZE_PX = 12;
const SLOT_CHECK_STROKE_WIDTH = 3;

const SlotFallbackVisual = <T extends EntityBase>({
  record,
  fallback: { role, rawValue },
}: {
  record: T;
  fallback: SlotFallback;
}) => {
  switch (role.visual) {
    case SlotVisual.Initials:
      return (
        <ColoredAvatar
          name={extractDisabledUserTextFromName(String(rawValue))}
          size="small"
          sx={{ fontWeight: 700 }}
        />
      );

    case SlotVisual.ServiceLevelDot:
      return (
        <ServiceLevelDot
          serviceLevel={formatServiceLevelWithCpp(String(rawValue), getRowCppExpDate(record))}
          dotSize={SLOT_DOT_SIZE}
        />
      );

    case SlotVisual.Icon:
      return (
        <DynamicIcon
          name={role.getIcon?.(rawValue) ?? ''}
          library="lucide"
          size={SLOT_ICON_SIZE_PX}
        />
      );

    case SlotVisual.Check:
      return (
        <SlotCheck>
          <DynamicIcon
            name="check"
            library="lucide"
            size={SLOT_CHECK_ICON_SIZE_PX}
            strokeWidth={SLOT_CHECK_STROKE_WIDTH}
          />
        </SlotCheck>
      );
  }
};

/** The status when Status is on; otherwise the first displayed attribute of the fallback priority that has something to show, or nothing. */
export const CardSlotContent = <T extends EntityBase>({
  record,
  layout,
  cardColumns,
  customComponentsByAttribute,
}: {
  record: T;
  layout: CardLayoutRoles;
  cardColumns: CardColumns;
  customComponentsByAttribute?: CustomComponentsByAttribute;
}) => {
  if (cardColumns.slot) {
    return (
      <AttributeValue
        record={record}
        column={cardColumns.slot}
        customComponentsByAttribute={customComponentsByAttribute}
      />
    );
  }

  const fallback = findSlotFallback(cardColumns, layout, (column) =>
    getAttributeRawValue(record, column),
  );

  return fallback ? <SlotFallbackVisual record={record} fallback={fallback} /> : null;
};
